/**
 * Motor del cuestionario.
 *
 * Construye, para una entrevista concreta, la secuencia exacta de pantallas a
 * partir de la definición del instrumento: resuelve las rotaciones, expande las
 * baterías a sus filas, aplica la lógica condicional y los recortes de §8.1, y
 * detecta las condiciones que terminan la entrevista.
 *
 * Por qué un motor y no pantallas escritas a mano: §9.6 exige que los reactivos
 * del núcleo mantengan exactamente la misma redacción, escala y ORDEN en todas
 * las oleadas. Con pantallas escritas a mano, la oleada W2 divergiría de la W1
 * en algún detalle y la serie de tiempo dejaría de ser comparable. Con un motor
 * que lee la definición versionada, la comparabilidad es verificable.
 */

import {
  KNOWS_PERSONAJE_NEGATIVE_CODE,
  createRng,
  rotateRows,
  seedFor,
  selectSubsampleRows,
  type RotatableRow,
} from '@capi/shared';

import type {
  DraftAnswer,
  DraftInterview,
} from '../db/db';
import type {
  FieldPackage,
  InstrumentBlock,
  InstrumentOption,
  InstrumentQuestion,
} from '../api/client';

/* -------------------------------------------------------------------------- */
/*                                   Tipos                                    */
/* -------------------------------------------------------------------------- */

export interface Row {
  code: string;
  label: string;
  rotates?: boolean;
  rotationGroup?: string;
}

/** Una pantalla del cuestionario. */
export interface Step {
  /** Identificador único de la pantalla dentro de la entrevista. */
  id: string;
  block: InstrumentBlock;
  question: InstrumentQuestion;
  /** Texto final, con los marcadores [personaje] y [etiqueta] sustituidos. */
  text: string;
  /** Opciones en el orden en que deben leerse. */
  options: InstrumentOption[];
  /** Filas de la batería, ya rotadas y filtradas. Vacío si no es batería. */
  rows: Row[];
  /** Fila concreta cuando la batería se presenta fila por fila. */
  row: Row | null;
  /** La pantalla presenta la batería completa en una sola vista. */
  isMatrix: boolean;
  /** Clave con la que se guarda la respuesta. */
  answerKey: string;
}

export interface EngineContext {
  pkg: FieldPackage;
  draft: DraftInterview;
}

/* -------------------------------------------------------------------------- */
/*                            Claves de respuesta                             */
/* -------------------------------------------------------------------------- */

export function answerKey(questionCode: string, rowCode?: string | null, rankOrder?: number | null): string {
  let key = questionCode;
  if (rowCode) key += `::${rowCode}`;
  if (rankOrder !== null && rankOrder !== undefined) key += `::m${rankOrder}`;
  return key;
}

/* -------------------------------------------------------------------------- */
/*                          Evaluación de condiciones                         */
/* -------------------------------------------------------------------------- */

type Condition = Record<string, unknown>;

/**
 * Evalúa una condición del instrumento contra las respuestas capturadas.
 *
 * `rowCode` es el contexto de la fila actual, necesario para `knowsRowPersonaje`:
 * I2–I5 sólo se aplican a los personajes que el entrevistado declaró conocer en
 * I1, y eso se decide fila por fila.
 */
export function evaluateCondition(
  condition: unknown,
  ctx: EngineContext,
  rowCode?: string | null,
): boolean {
  if (!condition || typeof condition !== 'object') return true;
  const c = condition as Condition;
  const answers = ctx.draft.answers;

  const valueOf = (questionCode: string): string | number | null => {
    const direct = answers[answerKey(questionCode)];
    if (!direct) return null;
    if (direct.optionCode !== null && direct.optionCode !== undefined) return direct.optionCode;
    if (direct.numericValue !== null && direct.numericValue !== undefined) return direct.numericValue;
    if (direct.textValue !== null && direct.textValue !== undefined) return direct.textValue;
    return null;
  };

  switch (c['op']) {
    case 'equals':
      return valueOf(c['question'] as string) === c['value'];
    case 'notEquals':
      return valueOf(c['question'] as string) !== c['value'];
    case 'in':
      return (c['values'] as (string | number)[]).includes(valueOf(c['question'] as string) as never);
    case 'notIn':
      return !(c['values'] as (string | number)[]).includes(valueOf(c['question'] as string) as never);
    case 'gte': {
      const v = valueOf(c['question'] as string);
      return typeof v === 'number' && v >= (c['value'] as number);
    }
    case 'lte': {
      const v = valueOf(c['question'] as string);
      return typeof v === 'number' && v <= (c['value'] as number);
    }
    case 'answered':
      return valueOf(c['question'] as string) !== null;
    case 'rowIn': {
      const answer = answers[answerKey(c['question'] as string, c['row'] as string)];
      return Boolean(answer?.optionCode && (c['values'] as string[]).includes(answer.optionCode));
    }
    case 'knowsRowPersonaje': {
      // La fila es un personaje; se consulta su respuesta en I1.
      if (!rowCode) return false;
      const i1 = answers[answerKey('I1', rowCode)];
      return Boolean(i1?.optionCode && i1.optionCode !== 'no_conoce');
    }
    case 'assignedTo':
      return ctx.draft.experimentCellId === c['cell'];
    case 'trimEnabled':
      return ctx.draft.trimState[c['trim'] as string] === true;
    case 'and':
      return (c['all'] as unknown[]).every((x) => evaluateCondition(x, ctx, rowCode));
    case 'or':
      return (c['any'] as unknown[]).some((x) => evaluateCondition(x, ctx, rowCode));
    case 'not':
      return !evaluateCondition(c['condition'], ctx, rowCode);
    default:
      return true;
  }
}

/* -------------------------------------------------------------------------- */
/*                          Resolución de filas                               */
/* -------------------------------------------------------------------------- */

/**
 * Resuelve las filas de una batería en el orden en que deben presentarse.
 *
 * Las fuentes de personajes usan SIEMPRE `draft.personajeOrder`, que es el
 * orden que el servidor fijó al abrir la entrevista. §Bloque 3 exige que el
 * CAPI conserve el mismo orden en todas las preguntas del bloque, y §Bloque 9
 * que la tarjeta de vinculación use ese mismo orden: por eso no se vuelve a
 * rotar aquí.
 */
export function resolveRows(question: InstrumentQuestion, ctx: EngineContext): Row[] {
  const source = question.rowSource;
  if (!source) return [];

  const personajeLabel = (id: string): string =>
    ctx.pkg.personajes.find((p) => p.id === id)?.cardName ?? id;

  let rows: Row[];

  switch (source.kind) {
    case 'personajes':
      rows = ctx.draft.personajeOrder.map((id) => ({ code: id, label: personajeLabel(id) }));
      break;

    case 'personajes-known':
      // Sólo los conocidos, conservando el orden rotado de la entrevista.
      rows = ctx.draft.personajeOrder
        .filter((id) => {
          const i1 = ctx.draft.answers[answerKey('I1', id)];
          return Boolean(i1?.optionCode && i1.optionCode !== KNOWS_PERSONAJE_NEGATIVE_CODE);
        })
        .map((id) => ({ code: id, label: personajeLabel(id) }));
      break;

    case 'personajes-assigned':
      /**
       * Submuestra que asignó el servidor para I6, filtrada otra vez por lo
       * que I1 dice AHORA.
       *
       * El doble filtro no es redundante: la asignación se pide al completar
       * I1 y queda fija, pero el encuestador puede volver atrás y corregir.
       * Sin este filtro, una corrección de «lo conozco» a «no lo conozco»
       * dejaba a I6 preguntando «¿qué es lo primero que le viene a la mente?»
       * sobre alguien que el entrevistado acaba de decir que no conoce.
       */
      rows = ctx.draft.i6PersonajeIds
        .filter((id) => {
          const i1 = ctx.draft.answers[answerKey('I1', id)];
          return Boolean(i1?.optionCode && i1.optionCode !== KNOWS_PERSONAJE_NEGATIVE_CODE);
        })
        .map((id) => ({ code: id, label: personajeLabel(id) }));
      break;

    case 'parties':
      rows = ctx.draft.partyOrder.map((id) => ({
        code: id,
        label: ctx.pkg.parties.find((p) => p.id === id)?.label ?? id,
      }));
      break;

    case 'parties-plus-jhh':
      // Los 9 partidos rotados y, al final y sin rotar, la coalición.
      rows = [
        ...ctx.draft.partyOrder.map((id) => ({
          code: id,
          label: ctx.pkg.parties.find((p) => p.id === id)?.label ?? id,
        })),
        {
          code: 'JHH',
          label:
            ctx.pkg.coalitions.find((c) => c.id === 'JHH')?.spokenLabel ??
            'la coalición Juntos Haremos Historia: MORENA, PVEM y PT',
          rotates: false,
        },
      ];
      break;

    case 'officials':
      rows = ctx.pkg.block4Officials.map((o) => ({ code: o.questionCode, label: o.name }));
      break;

    case 'literal': {
      const declared = (source.rows ?? []).map<Row>((r) => ({
        code: r.code,
        label: r.label,
        ...(r.rotates !== undefined ? { rotates: r.rotates } : {}),
        ...(r.rotationGroup !== undefined ? { rotationGroup: r.rotationGroup } : {}),
      }));
      rows = question.rotateRows
        ? rotateRows<RotatableRow & Row>(
            declared as (RotatableRow & Row)[],
            createRng(seedFor(ctx.draft.clientUuid, `rows:${question.code}`)),
          )
        : declared;
      break;
    }

    default:
      rows = [];
  }

  // Filtro por fila (I2-I5 sobre conocidos).
  if (question.showRowIf) {
    rows = rows.filter((row) => evaluateCondition(question.showRowIf, ctx, row.code));
  }

  // Recortes de §8.1 que reducen filas (Bloque 9 a 3 de 5 temas).
  const trim = question.trimKey
    ? ctx.pkg.trims.find((t) => t.key === question.trimKey)
    : undefined;
  if (trim?.enabled && trim.mode === 'subsample-rows' && trim.rowsKept) {
    rows = selectSubsampleRows(ctx.draft.clientUuid, trim.key, rows, trim.rowsKept);
  }

  return rows;
}

/* -------------------------------------------------------------------------- */
/*                            Resolución de opciones                          */
/* -------------------------------------------------------------------------- */

export function resolveOptions(question: InstrumentQuestion, ctx: EngineContext): InstrumentOption[] {
  // Las opciones de cierre automático las escribe el servidor y NO se dibujan:
  // si el encuestador pudiera elegir "no lo conoce" en I2–I5 estaría
  // registrando a mano un valor que significa otra cosa —que la fila ni
  // siquiera debió preguntarse— y ensuciaría la base de §10.1.4.
  let options = (question.options ?? []).filter((o) => !o.systemAssigned);

  // V1-V6: las opciones son los personajes en el orden de la entrevista.
  if (question.kind === 'matrix-single' && options.length === 0) {
    options = [
      ...ctx.draft.personajeOrder.map((id) => ({
        code: id,
        label: ctx.pkg.personajes.find((p) => p.id === id)?.cardName ?? id,
      })),
      { code: 'ninguno', label: 'Ninguno' },
      { code: 'ns', label: 'NS', nonSubstantive: true },
    ];
    return options;
  }

  if (question.rotateOptions) {
    const rotatable = options.map((o) => ({
      ...o,
      // NS/NC y "Otro" NO rotan: van siempre al final, porque son salidas de
      // la escala y no alternativas sustantivas que compitan entre sí.
      rotates: !(o.nonSubstantive || o.code === 'otro' || o.code === 'ninguno'),
    }));
    options = rotateRows<RotatableRow & InstrumentOption>(
      rotatable as (RotatableRow & InstrumentOption)[],
      createRng(seedFor(ctx.draft.clientUuid, `options:${question.code}`)),
    );
  }

  return options;
}

/* -------------------------------------------------------------------------- */
/*                         Sustitución de marcadores                          */
/* -------------------------------------------------------------------------- */

function fillPlaceholders(
  text: string,
  ctx: EngineContext,
  row: Row | null,
  question: InstrumentQuestion,
): string {
  let out = text;

  if (row) {
    out = out
      .replace(/\[personaje\]/g, row.label)
      .replace(/\[escenario\]/g, row.label)
      .replace(/\[tema\]/g, row.label)
      .replace(/\[partido\]/g, row.label);
  }

  // X1 · el personaje y la etiqueta vienen de la celda asignada.
  if (question.code === 'X1' && ctx.draft.experimentCell) {
    const personaje =
      ctx.pkg.personajes.find((p) => p.id === ctx.draft.experimentCell!.personajeId)?.cardName ??
      ctx.draft.experimentCell.personajeId;
    out = out
      .replace(/\[personaje\]/g, personaje)
      .replace(/\[etiqueta\]/g, ctx.draft.experimentCell.labelText);
  }

  return out;
}

/* -------------------------------------------------------------------------- */
/*                          Construcción de la secuencia                      */
/* -------------------------------------------------------------------------- */

/**
 * Preguntas cuya batería se presenta FILA POR FILA en lugar de en una matriz.
 *
 * Criterio: se presenta fila por fila cuando el reactivo exige leer un texto
 * distinto por fila, porque la lectura debe ser idéntica para todos los
 * entrevistados (§9.6) y una matriz invita al encuestador a abreviar. I1 va en
 * matriz porque es un barrido rápido de la tarjeta completa a la vista,
 * que es justamente como lo describe §Bloque 3.
 */
const ROW_BY_ROW_QUESTIONS = new Set(['I6', 'V']);

export function buildSteps(ctx: EngineContext): Step[] {
  const steps: Step[] = [];

  for (const block of ctx.pkg.definition.blocks) {
    for (const question of block.questions) {
      // Lógica condicional a nivel de pregunta.
      if (question.showIf && !evaluateCondition(question.showIf, ctx)) continue;

      // Recorte de §8.1 que excluye la pregunta para este entrevistado.
      if (question.trimKey) {
        const trim = ctx.pkg.trims.find((t) => t.key === question.trimKey);
        if (
          trim?.enabled &&
          trim.mode === 'subsample-respondents' &&
          ctx.draft.trimState[trim.key] === false
        ) {
          continue;
        }
      }

      // X1 sólo si el entrevistado conoce al personaje de su celda.
      if (question.code === 'X1') {
        const cell = ctx.draft.experimentCell;
        if (!cell) continue;
        const i1 = ctx.draft.answers[answerKey('I1', cell.personajeId)];
        if (!i1?.optionCode || i1.optionCode === 'no_conoce') continue;
      }

      const options = resolveOptions(question, ctx);
      const rows = resolveRows(question, ctx);
      const isBattery = Boolean(question.rowSource);

      if (!isBattery) {
        steps.push({
          id: question.code,
          block,
          question,
          text: fillPlaceholders(question.text, ctx, null, question),
          options,
          rows: [],
          row: null,
          isMatrix: false,
          answerKey: answerKey(question.code),
        });
        continue;
      }

      if (rows.length === 0) continue;

      if (ROW_BY_ROW_QUESTIONS.has(question.code)) {
        for (const row of rows) {
          steps.push({
            id: `${question.code}:${row.code}`,
            block,
            question,
            text: fillPlaceholders(question.text, ctx, row, question),
            options,
            rows,
            row,
            isMatrix: false,
            answerKey: answerKey(question.code, row.code),
          });
        }
      } else {
        steps.push({
          id: question.code,
          block,
          question,
          text: fillPlaceholders(question.text, ctx, null, question),
          options,
          rows,
          row: null,
          isMatrix: true,
          answerKey: answerKey(question.code),
        });
      }
    }
  }

  return steps;
}

/* -------------------------------------------------------------------------- */
/*                               Terminación                                  */
/* -------------------------------------------------------------------------- */

export interface Termination {
  disposition: string;
  message: string;
  questionCode: string;
}

/**
 * Comprueba si una respuesta recién capturada termina la entrevista.
 *
 * §Bloque 0 declara tres cortes: F2 = 0 (no hay adultos elegibles), F4 = no
 * (la persona no cumple el universo) y F6 = no acepta (sin consentimiento).
 */
export function checkTermination(question: InstrumentQuestion, ctx: EngineContext): Termination | null {
  if (!question.terminateIf) return null;
  if (!evaluateCondition(question.terminateIf.condition, ctx)) return null;
  return {
    disposition: question.terminateIf.disposition,
    message: question.terminateIf.message,
    questionCode: question.code,
  };
}

/* -------------------------------------------------------------------------- */
/*                               Validación                                   */
/* -------------------------------------------------------------------------- */

export interface ValidationResult {
  valid: boolean;
  message?: string;
}

/** Valida la respuesta de una pantalla antes de permitir avanzar. */
export function validateStep(
  step: Step,
  answers: Record<string, DraftAnswer>,
): ValidationResult {
  const { question } = step;

  const has = (key: string): boolean => {
    const a = answers[key];
    if (!a) return false;
    return (
      (a.optionCode !== null && a.optionCode !== undefined && a.optionCode !== '') ||
      (a.numericValue !== null && a.numericValue !== undefined) ||
      (a.textValue !== null && a.textValue !== undefined && a.textValue.trim() !== '')
    );
  };

  // Multi y ranked guardan una entrada por selección; se busca por prefijo.
  const hasAny = (prefix: string): boolean =>
    Object.keys(answers).some((k) => (k === prefix || k.startsWith(`${prefix}::`)) && has(k));

  if (step.isMatrix) {
    const missing = step.rows.filter((row) => !has(answerKey(question.code, row.code)));
    if (question.required && missing.length > 0) {
      return {
        valid: false,
        message:
          missing.length === step.rows.length
            ? 'Captura la respuesta de cada renglón.'
            : `Faltan ${missing.length} de ${step.rows.length}: ${missing.slice(0, 3).map((m) => m.label).join(', ')}${missing.length > 3 ? '…' : ''}`,
      };
    }
    return { valid: true };
  }

  if (!question.required) return { valid: true };

  // Estas tres reparten la respuesta en varias claves (una por opción o por
  // casilla de mención), así que la obligatoriedad se cumple con que haya AL
  // MENOS una, no con que exista la clave base —que nunca se escribe—.
  if (
    question.kind === 'multi' ||
    question.kind === 'ranked-open' ||
    question.kind === 'open-multi'
  ) {
    return hasAny(step.answerKey)
      ? { valid: true }
      : { valid: false, message: 'Captura al menos una respuesta.' };
  }

  if (!has(step.answerKey)) {
    return { valid: false, message: 'Esta pregunta es obligatoria.' };
  }

  const answer = answers[step.answerKey]!;

  if (question.kind === 'numeric' || question.kind === 'scale') {
    const value = answer.numericValue;
    if (value === null || value === undefined || Number.isNaN(value)) {
      return { valid: false, message: 'Escribe un número.' };
    }
    if (question.min !== undefined && value < question.min) {
      return { valid: false, message: `El valor mínimo es ${question.min}.` };
    }
    if (question.max !== undefined && value > question.max) {
      return { valid: false, message: `El valor máximo es ${question.max}.` };
    }
    if (question.kind === 'numeric' && !Number.isInteger(value)) {
      return { valid: false, message: 'Escribe un número entero.' };
    }
  }

  // "Otro, especificar" exige el texto.
  const option = step.options.find((o) => o.code === answer.optionCode);
  if (option?.requiresSpecify && !answer.specifyText?.trim()) {
    return { valid: false, message: 'Especifica la respuesta.' };
  }

  return { valid: true };
}

/* -------------------------------------------------------------------------- */
/*                                 Progreso                                   */
/* -------------------------------------------------------------------------- */

export interface Progress {
  stepIndex: number;
  totalSteps: number;
  blockId: number;
  blockTitle: string;
  share: number;
  /** Minutos transcurridos desde el inicio. */
  elapsedMinutes: number;
  /** Duración de diseño, para que el encuestador calibre el ritmo. */
  designMinutes: { min: number; max: number };
}

export function computeProgress(
  steps: readonly Step[],
  index: number,
  startedAt: string,
  designMinutes: { min: number; max: number },
): Progress {
  const step = steps[Math.min(index, steps.length - 1)];
  return {
    stepIndex: index,
    totalSteps: steps.length,
    blockId: step?.block.id ?? 0,
    blockTitle: step?.block.title ?? '',
    share: steps.length > 0 ? index / steps.length : 0,
    elapsedMinutes: (Date.now() - new Date(startedAt).getTime()) / 60_000,
    designMinutes,
  };
}

/** Personajes que el entrevistado declaró conocer, para la asignación de I6. */
export function knownPersonajes(ctx: EngineContext): string[] {
  return ctx.draft.personajeOrder.filter((id) => {
    const i1 = ctx.draft.answers[answerKey('I1', id)];
    return Boolean(i1?.optionCode && i1.optionCode !== KNOWS_PERSONAJE_NEGATIVE_CODE);
  });
}

/**
 * Qué contestó I1 para cada personaje conocido.
 *
 * Lo necesita la asignación de I6: no es lo mismo conocer a alguien bien que
 * haber oído su nombre, y sólo el primero sostiene una asociación espontánea.
 */
export function personajeAwareness(ctx: EngineContext): Record<string, string> {
  const out: Record<string, string> = {};
  for (const id of ctx.draft.personajeOrder) {
    const i1 = ctx.draft.answers[answerKey('I1', id)];
    if (i1?.optionCode) out[id] = i1.optionCode;
  }
  return out;
}

/** Convierte el borrador a las respuestas que espera el API. */
export function toApiAnswers(draft: DraftInterview): DraftAnswer[] {
  return Object.values(draft.answers).filter((answer) => {
    const hasValue =
      (answer.optionCode !== null && answer.optionCode !== undefined && answer.optionCode !== '') ||
      (answer.numericValue !== null && answer.numericValue !== undefined) ||
      (answer.textValue !== null && answer.textValue !== undefined && answer.textValue.trim() !== '');
    return hasValue;
  });
}
